import React from "react";
import {
  CheckCircle2,
  CircleDollarSign,
  Clock3,
  Landmark,
  XCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { SettlementSummaryBar } from "@/components/pages/settlement/components/SettlementSummaryBar";
import { StatCard } from "@/components/pages/settlement/components/StatCard";
import { formatMoney } from "@/components/pages/reservations/utils/formatMoney";
import type { BillingTransactionStats } from "@/types/billing-transactions";

interface BillingTransactionsStatsProps {
  isLoading?: boolean;
  stats?: BillingTransactionStats;
}

export const BillingTransactionsStats = ({
  isLoading = false,
  stats,
}: BillingTransactionsStatsProps): React.JSX.Element => {
  const { t } = useTranslation();

  const currency =
    stats?.currency && stats.currency.trim() !== "" ? stats.currency : "EUR";

  const fallbackValue = isLoading ? t("common.loading") : "-";

  const statItems = [
    {
      icon: <Landmark className="size-6 text-light-stone" />,
      key: "totalRevenue",
      label: t("billing.guestTransactionsStats.totalRevenue"),
      value: stats ? formatMoney(stats.totalRevenue, currency) : fallbackValue,
      valueClassName: "text-eathy-comfort-2",
    },
    {
      icon: <CheckCircle2 className="size-6 text-light-stone" />,
      key: "successfulTransactions",
      label: t("billing.guestTransactionsStats.successfulTransactions"),
      value: stats
        ? stats.succeededCount.toLocaleString("en-US")
        : fallbackValue,
      valueClassName: "text-eathy-comfort-2",
    },
    {
      icon: <XCircle className="size-6 text-light-stone" />,
      key: "failedTransactions",
      label: t("billing.guestTransactionsStats.failedTransactions"),
      value: stats ? stats.failedCount.toLocaleString("en-US") : fallbackValue,
      valueClassName: "text-destructive",
    },
    {
      icon: <Clock3 className="size-6 text-light-stone" />,
      key: "pendingTransactions",
      label: t("billing.guestTransactionsStats.pendingTransactions"),
      value: stats ? stats.pendingCount.toLocaleString("en-US") : fallbackValue,
      valueClassName: "text-foreground",
    },
    {
      icon: <CircleDollarSign className="size-6 text-light-stone" />,
      key: "averageCheck",
      label: t("billing.guestTransactionsStats.averageCheck"),
      value: stats ? formatMoney(stats.averageCheck, currency) : fallbackValue,
      valueClassName: "text-eathy-comfort-2",
    },
  ];

  return (
    <SettlementSummaryBar>
      <div className="grid w-full flex-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {statItems.map((item) => (
          <StatCard
            key={item.key}
            icon={item.icon}
            label={item.label}
            value={item.value}
            valueClassName={item.valueClassName}
          />
        ))}
      </div>
    </SettlementSummaryBar>
  );
};
